/**
 * Atualiza preços dos cards Belabelly ao trocar variante (swatch) — layout categoria ML.
 */
import { ThemeEvents } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * @param {HTMLElement} card
 */
function getPricingRoot(card) {
  return card.querySelector('[data-belabelly-pricing]');
}

/**
 * @param {number} priceCents
 * @param {number} installments
 */
function installmentCents(priceCents, installments) {
  const count = Math.max(1, installments);
  return Math.round((priceCents + Math.floor(count / 2)) / count);
}

/**
 * @param {HTMLElement} card
 * @param {{ price: number, compare_at_price?: number | null }} variant
 */
function updateCardPricing(card, variant) {
  const meta = getPricingRoot(card);
  if (!meta || !variant || variant.price == null) return;
  const visible = meta;

  const price = Number(variant.price);
  const compare =
    variant.compare_at_price && variant.compare_at_price > price ? Number(variant.compare_at_price) : null;
  const pixPctNum = Number(meta.dataset.pixPercent || 5);
  const pixPct = pixPctNum / 100;
  const showPix = pixPctNum > 0;
  const instCount = Number(meta.dataset.installmentCount || 10);
  const moneyFormat = meta.dataset.moneyFormat || '{{amount}}';

  const pix = showPix ? Math.round(price * (1 - pixPct)) : price;
  const displayCents = showPix ? pix : price;
  const inst = installmentCents(price, instCount);

  let discountPct = 0;
  let showPixOffLabel = false;
  if (compare && compare > displayCents) {
    discountPct = Math.round(((compare - displayCents) * 100) / compare);
    showPixOffLabel = showPix;
  } else if (showPix) {
    discountPct = pixPctNum;
    showPixOffLabel = true;
  }

  const compareEl = visible.querySelector('[data-md-card-compare]');
  const discountLabel = visible.querySelector('[data-md-card-discount-pill]');
  const instEl = visible.querySelector('[data-md-card-installments]');
  const wholeEl = visible.querySelector('[data-md-card-price-whole]');
  const centsEl = visible.querySelector('[data-md-card-price-cents]');
  const fullEl = visible.querySelector('[data-md-card-price-display]');
  const unavailable = visible.querySelector('.ml-card-info__unavailable');

  if (price <= 0) {
    if (unavailable) unavailable.classList.remove('hidden');
    visible.querySelector('.ml-card-info__price-row')?.classList.add('hidden');
    instEl?.classList.add('hidden');
    visible.querySelector('[data-md-card-shipping]')?.classList.add('hidden');
    return;
  }

  if (unavailable) unavailable.classList.add('hidden');
  visible.querySelector('.ml-card-info__price-row')?.classList.remove('hidden');

  const whole = Math.floor(displayCents / 100);
  const cents = displayCents % 100;
  const centsStr = cents < 10 ? `0${cents}` : String(cents);

  if (fullEl) {
    fullEl.textContent = formatMoney(displayCents, moneyFormat);
  } else if (wholeEl && centsEl) {
    wholeEl.textContent = String(whole);
    centsEl.textContent = centsStr;
  }

  if (compareEl) {
    if (compare) {
      compareEl.textContent = formatMoney(compare, moneyFormat);
      compareEl.classList.remove('hidden');
      compareEl.removeAttribute('hidden');
    } else {
      compareEl.classList.add('hidden');
      compareEl.setAttribute('hidden', '');
    }
  }

  const tplPixOff = meta.dataset.templatePixOff || '__PCT__% OFF no Pix';
  if (discountLabel) {
    if (!compare && discountPct > 0 && showPixOffLabel) {
      discountLabel.textContent = tplPixOff.replace('__PCT__', String(discountPct));
      discountLabel.classList.remove('hidden');
      discountLabel.dataset.pixOffLabel = 'true';
    } else {
      discountLabel.classList.add('hidden');
    }
  }

  const tplInst = meta.dataset.templateInstallments || '';
  if (instEl && tplInst) {
    if (inst > 0) {
      instEl.textContent = tplInst
        .replace('__COUNT__', String(instCount))
        .replace('__PRICE__', formatMoney(inst, moneyFormat));
      instEl.classList.remove('hidden');
    } else {
      instEl.classList.add('hidden');
    }
  }

  const shipEl = visible.querySelector('[data-md-card-shipping]');
  const freeMin = Number(shipEl?.dataset.freeShippingMin || 0);
  if (shipEl) {
    const qualifies = freeMin > 0 && price >= freeMin;
    shipEl.textContent = qualifies ? shipEl.dataset.labelFree || '' : '';
    shipEl.classList.toggle('hidden', !qualifies);
  }

  const dealRoot = card.querySelector('[data-belabelly-deal-tags]');
  if (dealRoot) {
    const salePct = compare && compare > price ? Math.round(((compare - price) * 100) / compare) : 0;
    const saveCents = compare && compare > price ? compare - price : 0;
    const until = dealRoot.dataset.offUntil !== 'false';
    const tplOff = until
      ? dealRoot.dataset.templateOffUntil || 'ATÉ __PCT__% OFF'
      : dealRoot.dataset.templateOff || '__PCT__% OFF';
    const tplSave = dealRoot.dataset.templateSave || 'Economize __AMOUNT__';
    const offPill = dealRoot.querySelector('[data-deal-off-pill]');
    const savePill = dealRoot.querySelector('[data-deal-save-pill]');

    if (offPill) {
      if (salePct > 0) {
        offPill.textContent = tplOff.replace('__PCT__', String(salePct));
        offPill.classList.remove('hidden');
      } else {
        offPill.classList.add('hidden');
      }
    }

    if (savePill) {
      if (saveCents > 0) {
        savePill.textContent = tplSave.replace('__AMOUNT__', formatMoney(saveCents, moneyFormat));
        savePill.classList.remove('hidden');
      } else {
        savePill.classList.add('hidden');
      }
    }
  }

  meta.dataset.variantPrice = String(price);
  meta.dataset.variantCompare = String(compare || 0);
  if (variant.id) meta.dataset.variantId = String(variant.id);

  const buybar = card.querySelector('[data-belabelly-buybar]');
  if (buybar && price > 0) {
    const barPrice = buybar.querySelector('[data-buybar-price]');
    const barInst = buybar.querySelector('[data-buybar-installments]');
    if (barPrice) barPrice.textContent = formatMoney(price, moneyFormat);
    if (barInst) {
      const instBar = installmentCents(price, 3);
      const tplInstOu = meta.dataset.templateInstallments || '';
      if (tplInstOu) {
        barInst.textContent = tplInstOu
          .replace('__COUNT__', '3')
          .replace('__PRICE__', formatMoney(instBar, moneyFormat));
      }
    }
  }
}

document.addEventListener(ThemeEvents.variantUpdate, (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const card = target.closest('product-card.belabelly-ml-card, product-card.belabelly-product-card');
  if (!card || !event.detail?.resource) return;

  updateCardPricing(card, event.detail.resource);
});
